import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import socketService from './socketService';
import Events from './events';

const SocketContext = createContext(null);

/**
 * SocketProvider manages socket lifecycle synchronized with authentication state
 * and broadcasts connection state changes down the component tree.
 */
export const SocketProvider = ({ children, authToken, authOptions = {} }) => {
  const [isConnected, setIsConnected] = useState(socketService.isConnected);

  useEffect(() => {
    // 1. Establish connection if token exists
    if (authToken) {
      socketService.connectSocket({
        token: authToken,
        ...authOptions,
      });
    } else {
      // Cleanly teardown when no token is present (logout / unauthenticated)
      socketService.disconnectSocket();
    }

    // 2. Track connection lifecycle
    const unsubConnect = socketService.addListener(Events.CONNECT, () => {
      setIsConnected(true);
    });

    const unsubDisconnect = socketService.addListener(Events.DISCONNECT, () => {
      setIsConnected(false);
    });

    const unsubError = socketService.addListener(Events.CONNECT_ERROR, () => {
      setIsConnected(false);
    });

    // Cleanup on unmount or token change
    return () => {
      unsubConnect();
      unsubDisconnect();
      unsubError();
      socketService.disconnectSocket();
    };
  }, [authToken, JSON.stringify(authOptions)]);

  const connect = (token, options = {}) => {
    socketService.connectSocket({ token, ...options });
  };

  const disconnect = () => {
    socketService.disconnectSocket();
  };

  const contextValue = useMemo(
    () => ({
      isConnected,
      socketService,
      connect,
      disconnect,
      socketId: socketService.socketId,
    }),
    [isConnected]
  );

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

/**
 * Hook to access the Socket Service and real-time connection status
 */
export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

/**
 * Hook to subscribe to a Socket Event with automatic cleanup on unmount or deps change
 * @param {string} event - Socket event name
 * @param {Function} handler - Message listener callback
 * @param {Array} deps - Dependency array for re-binding
 */
export const useSocketEvent = (event, handler, deps = []) => {
  useEffect(() => {
    if (!event || typeof handler !== 'function') return;

    const unsubscribe = socketService.addListener(event, handler);

    return () => {
      unsubscribe();
    };
  }, [event, ...deps]);
};

export default SocketContext;
